import IUnityTransport from '../unityView/transport/IUnityTransport';

/**
 * Runs the AI designer's Designer calls against the Unity instance on this page.
 *
 * The AI designer service runs the uber-llm-designer skill, whose scripts talk to "the Designer" over
 * the Unity Editor Dev Bridge's four routes (health, send, scripts, screenshot). The service relays
 * each call here over its WebSocket, and each is one WebInterface method: `send` names it,
 * `scripts` is SetScripts, `screenshot` is Screenshot. The driver sends it with
 * `SendMessage("WebInterface", ...)`, waits for its answer on `window.UCReplyReceived` /
 * `window.UCStyleResultReceived`, and hands that back in the Dev Bridge's envelope
 * `{ok, error, log, data?, diagnostics?}` (see the Unity project's Assets/Scripts/WebInterface.md).
 * It knows nothing of the skill.
 *
 * A build that predates SetScripts and Screenshot (GetApi says which it has) gets the same work done
 * the old way: the apply one SendMessage at a time, the screenshot read off the canvas.
 *
 * In Unity editor mode (admin dev tool) the Editor's own Dev Bridge answers, so the calls go straight
 * to it: the page's editor transport de-duplicates and debounces sends, which would swallow the
 * scripts' repeated reads.
 *
 * Calls run one at a time. Replies are tagged only by kind, and a read must not overtake the apply
 * before it - which is also why the Dev Bridge serialises them on the main thread.
 */

export type TBridgeRoute = 'health' | 'send' | 'scripts' | 'screenshot';

export interface IBridgeCall {
    id: number;
    route: TBridgeRoute;
    body: any;
}

/** What goes back to the service: the Dev Bridge's HTTP status and JSON body, or a PNG (base64). */
export interface IBridgeAnswer {
    status: number;
    body?: any;
    png?: string;
}

export interface IDriverHost {
    /** The page's Unity (the WebGL instance), or null while it loads. */
    instance: () => IUnityTransport | null;
    /** Unity has loaded its catalog (UCOnLoadedReceived); scripts applied before fail on boards. */
    loaded: () => boolean;
    /** Set in Unity editor mode: the Editor's Dev Bridge, which answers the calls itself. */
    editorUrl: string | null;
    /** The WebGL canvas, for a build that predates the Screenshot method. */
    canvas: () => HTMLCanvasElement | null;
}

const OBJECT = 'WebInterface';

/** Reads that answer on UCReplyReceived(kind, json). */
const REPLY_KINDS: Record<string, string> = {
    GetSchema: 'schema',
    GetHierarchy: 'hierarchy',
    GetOrder: 'order',
    PlaceElement: 'placement',
    Screenshot: 'screenshot',
    SetScripts: 'scripts',
    SetVisibility: 'visibility',
    GetVisibility: 'visibility',
    GetApi: 'api',
};

const READ_TIMEOUT = 20_000;
const APPLY_TIMEOUT = 180_000;
const SHOT_TIMEOUT = 30_000;
/** GetApi answers inside the SendMessage call; a build without it never does. */
const API_TIMEOUT = 3_000;

class TimeoutError extends Error {}

type TCallbackWindow = Window & {
    UCReplyReceived?: (kind: string, json: string) => void;
    UCStyleResultReceived?: (json: string) => void;
};

const envelope = (ok: boolean, error: string | null, extra: Record<string, unknown> = {}) =>
    ({ ok, error, log: [] as string[], ...extra });

const parse = (json: string): any => {
    try {
        return JSON.parse(json);
    } catch {
        return json;
    }
};

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const nextFrame = () => new Promise<void>((resolve) =>
    typeof requestAnimationFrame === 'function' ? requestAnimationFrame(() => resolve()) : setTimeout(resolve, 16));

/** SendMessage takes one string or number; booleans go as "true"/"false", objects as JSON. */
const toArg = (arg: unknown): any => {
    if (arg === undefined || arg === null) return undefined;
    if (typeof arg === 'string' || typeof arg === 'number') return arg;
    if (typeof arg === 'boolean') return arg ? 'true' : 'false';
    return JSON.stringify(arg);
};

const toText = (value: unknown): string =>
    value === undefined || value === null ? '' : typeof value === 'string' ? value : JSON.stringify(value);

function toBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    }
    return btoa(binary);
}

/**
 * The two callbacks the driver listens on. Installed on window next to whatever the page set
 * (which is still called), and re-installed before each call in case something replaced them.
 */
class CallbackHub {
    private replyWaiters = new Map<string, ((json: string) => void)[]>();
    private styleWaiters: ((json: string) => void)[] = [];
    private styleWatchers = new Set<(json: string) => void>();
    private previousReply?: (kind: string, json: string) => void;
    private previousStyle?: (json: string) => void;
    /** Style results heard so far: every apply, the AI's or the admin's, reports one. */
    styleResults = 0;

    private readonly onReply = (kind: string, json: string) => {
        this.replyWaiters.get(kind)?.shift()?.(json);
        this.previousReply?.(kind, json);
    };

    private readonly onStyle = (json: string) => {
        this.styleResults += 1;
        this.styleWatchers.forEach((watch) => watch(json));
        this.styleWaiters.shift()?.(json);
        this.previousStyle?.(json);
    };

    install(): void {
        const w = window as TCallbackWindow;
        if (w.UCReplyReceived !== this.onReply) {
            this.previousReply = typeof w.UCReplyReceived === 'function' ? w.UCReplyReceived : undefined;
            w.UCReplyReceived = this.onReply;
        }
        if (w.UCStyleResultReceived !== this.onStyle) {
            this.previousStyle = typeof w.UCStyleResultReceived === 'function' ? w.UCStyleResultReceived : undefined;
            w.UCStyleResultReceived = this.onStyle;
        }
    }

    /** Runs `trigger` and resolves with the next reply of `kind`. Unity may answer inside `trigger`. */
    expectReply(kind: string, timeout: number, trigger: () => void): Promise<string> {
        const waiters = this.replyWaiters.get(kind) ?? [];
        this.replyWaiters.set(kind, waiters);
        return this.expect(waiters, timeout, trigger);
    }

    /** Runs `trigger` and resolves with the next style result. */
    expectStyle(timeout: number, trigger: () => void): Promise<string> {
        return this.expect(this.styleWaiters, timeout, trigger);
    }

    /** Every style result until the returned function is called. */
    watchStyle(watch: (json: string) => void): () => void {
        this.styleWatchers.add(watch);
        return () => this.styleWatchers.delete(watch);
    }

    private expect(waiters: ((json: string) => void)[], timeout: number, trigger: () => void): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const remove = () => {
                const i = waiters.indexOf(waiter);
                if (i >= 0) waiters.splice(i, 1);
            };
            const timer = setTimeout(() => {
                remove();
                reject(new TimeoutError('The Designer did not answer in time.'));
            }, timeout);
            const waiter = (json: string) => {
                clearTimeout(timer);
                resolve(json);
            };
            waiters.push(waiter);
            try {
                trigger();
            } catch (error) {
                clearTimeout(timer);
                remove();
                reject(error);
            }
        });
    }
}

class DesignerDriver {
    private chain: Promise<unknown> = Promise.resolve();
    private readonly hub = new CallbackHub();
    /** The methods this Unity build answers, asked once. */
    private api: Promise<Set<string>> | null = null;
    /** This page's part of the scene stamp: a reload starts a new count. */
    private readonly page = Math.random().toString(36).slice(2, 10);

    constructor(private readonly host: IDriverHost) {}

    /** Answers one relayed call. Health answers at once; the rest queue behind each other. */
    run(call: IBridgeCall): Promise<IBridgeAnswer> {
        if (call.route === 'health') {
            return this.safely(call);
        }
        const answer = this.chain.then(() => this.safely(call));
        this.chain = answer;
        return answer;
    }

    private async safely(call: IBridgeCall): Promise<IBridgeAnswer> {
        try {
            return this.host.editorUrl ? await this.viaEditor(this.host.editorUrl, call) : await this.viaPage(call);
        } catch (error) {
            if (error instanceof TimeoutError) {
                return { status: 504, body: envelope(false, error.message) };
            }
            return { status: 500, body: envelope(false, error instanceof Error ? error.message : String(error)) };
        }
    }

    // ===== Unity editor mode: the Dev Bridge answers =====
    private async viaEditor(url: string, call: IBridgeCall): Promise<IBridgeAnswer> {
        let response: Response;
        try {
            response = call.route === 'health'
                ? await fetch(`${url}/health`)
                : await fetch(`${url}/${call.route}`, {
                    method: 'POST',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify(call.body ?? {}),
                });
        } catch {
            return { status: 503, body: envelope(false, `The Unity Editor did not answer at ${url}.`, { playing: false }) };
        }
        if (call.route === 'screenshot' && response.ok
            && (response.headers.get('content-type') ?? '').startsWith('image/png')) {
            return { status: 200, png: toBase64(await response.arrayBuffer()) };
        }
        return { status: response.status, body: await response.json() };
    }

    // ===== WebGL: SendMessage in, callbacks out =====
    private async viaPage(call: IBridgeCall): Promise<IBridgeAnswer> {
        const unity = this.host.instance();
        if (!unity) {
            return { status: 503, body: envelope(false, 'The Designer has not loaded on the page yet.', { playing: false }) };
        }
        this.hub.install();
        switch (call.route) {
            case 'health':
                // `scene` changes with every apply, as the Dev Bridge's does: the scripts compare it
                // to the one their apply left to tell whether their pair is still what is shown
                return {
                    status: 200,
                    body: envelope(true, null, {
                        project: 'website', unityVersion: 'WebGL', playing: true, loaded: this.host.loaded(), liveLink: true,
                        scene: `page-${this.page}:${this.hub.styleResults}`,
                    }),
                };
            case 'send':
                return this.send(unity, call.body);
            case 'scripts':
                return this.scripts(unity, call.body);
            case 'screenshot':
                return this.screenshot(unity, call.body);
            default:
                return { status: 404, body: envelope(false, `Unknown route /${call.route}.`) };
        }
    }

    private async send(unity: IUnityTransport, body: any): Promise<IBridgeAnswer> {
        const method = String(body?.method ?? '');
        if (!method) {
            return { status: 400, body: envelope(false, 'No method.') };
        }
        const arg = toArg(body?.arg);
        const call = () => unity.SendMessage(OBJECT, method, arg);

        if (method === 'Screenshot') {
            return this.screenshot(unity, parse(toText(body?.arg)));
        }
        const kind = REPLY_KINDS[method];
        if (kind) {
            const json = await this.hub.expectReply(kind, READ_TIMEOUT, call);
            return { status: 200, body: envelope(true, null, { data: parse(json) }) };
        }
        if (method === 'GetStyleDiagnostics' || method === 'SetStyleScript') {
            const diagnostics = parse(await this.hub.expectStyle(APPLY_TIMEOUT, call));
            return {
                status: 200,
                body: envelope(true, null, method === 'GetStyleDiagnostics' ? { data: diagnostics, diagnostics } : { diagnostics }),
            };
        }
        // setters (FitToScreen, SetViewMode, SetDoorsOpen...) answer nothing
        call();
        return { status: 200, body: envelope(true, null, { result: null }) };
    }

    /** The methods this build answers (GetApi). A build older than GetApi answers nothing: none of the newer ones. */
    private methods(unity: IUnityTransport): Promise<Set<string>> {
        this.api ??= this.hub.expectReply('api', API_TIMEOUT, () => unity.SendMessage(OBJECT, 'GetApi'))
            .then((json) => new Set<string>(parse(json)?.methods ?? []))
            .catch(() => new Set<string>());
        return this.api;
    }

    /** The Dev Bridge's POST /scripts: SetScripts, whose answer is the same envelope. */
    private async scripts(unity: IUnityTransport, body: any): Promise<IBridgeAnswer> {
        const ucon = toText(body?.ucon ?? body?.uconScript);
        const style = toText(body?.style ?? body?.styleScript);
        if (!(await this.methods(unity)).has('SetScripts')) {
            return this.scriptsOneByOne(unity, ucon, style);
        }
        const result = parse(await this.hub.expectReply('scripts', APPLY_TIMEOUT,
            () => unity.SendMessage(OBJECT, 'SetScripts', JSON.stringify({ ucon, style }))));
        return { status: result?.ok ? 200 : 500, body: { log: [], ...result } };
    }

    /** For a build that predates SetScripts: its steps one SendMessage at a time. */
    private async scriptsOneByOne(unity: IUnityTransport, ucon: string, style: string): Promise<IBridgeAnswer> {
        // A UConScript that does not load answers at once, with a UconParseFailed style result;
        // one that loads answers nothing until the StyleScript has applied.
        let failure: string | null = null;
        const stop = this.hub.watchStyle((json) => { failure = json; });
        try {
            unity.SendMessage(OBJECT, 'SetUConScript', ucon);
        } finally {
            stop();
        }
        if (failure !== null) {
            const diagnostics = parse(failure);
            const message = diagnostics?.items?.[0]?.message ?? 'The UConScript could not be applied.';
            return { status: 500, body: envelope(false, message, { applied: false, diagnostics }) };
        }

        unity.SendMessage(OBJECT, 'FitToScreen');
        const diagnostics = parse(await this.hub.expectStyle(APPLY_TIMEOUT,
            () => unity.SendMessage(OBJECT, 'SetStyleScript', style)));
        return { status: 200, body: envelope(true, null, { applied: true, diagnostics }) };
    }

    private async screenshot(unity: IUnityTransport, body: any): Promise<IBridgeAnswer> {
        const options = body && typeof body === 'object' ? body : {};
        if (!(await this.methods(unity)).has('Screenshot')) {
            return this.canvasShot(unity, options);
        }
        const shot = parse(await this.hub.expectReply('screenshot', SHOT_TIMEOUT,
            () => unity.SendMessage(OBJECT, 'Screenshot', JSON.stringify(options))));
        return shot?.ok && shot.png
            ? { status: 200, png: shot.png }
            : { status: 500, body: envelope(false, shot?.error ?? 'The screenshot failed.') };
    }

    /**
     * For a build that predates Screenshot: the same camera moves, then the canvas as it is drawn -
     * with whatever outlines and gizmos are on. Needs the canvas's preserveDrawingBuffer (set for
     * admins in UnityView).
     */
    private async canvasShot(unity: IUnityTransport, options: any): Promise<IBridgeAnswer> {
        const canvas = this.host.canvas();
        if (!canvas) {
            return { status: 500, body: envelope(false, 'No Designer canvas on the page to take a screenshot of.') };
        }
        unity.SendMessage(OBJECT, 'SetRotating', 'false');
        unity.SendMessage(OBJECT, 'SetExploded', 'false');
        if (options.frame !== undefined && options.frame !== null) {
            const frame: Record<string, unknown> = { path: options.frame };
            for (const key of ['from', 'turn', 'elevation']) {
                if (options[key] !== undefined && options[key] !== null) frame[key] = options[key];
            }
            unity.SendMessage(OBJECT, 'FrameElement', JSON.stringify(frame));
        } else {
            // FitToScreen takes no view: SendMessage would drop it
            if (options.view) unity.SendMessage(OBJECT, 'SetViewMode', String(options.view));
            unity.SendMessage(OBJECT, 'FitToScreen');
        }
        unity.SendMessage(OBJECT, 'SetDoorsOpen', 'false');
        // the turntable, the exploded parts and the doors settle over a second or so
        await delay(1500);
        await nextFrame();
        await nextFrame();

        const clamp = (v: unknown, fallback: number) => Math.min(4096, Math.max(64, Number(v) || fallback));
        const width = clamp(options.width, 1280);
        const height = clamp(options.height, 720);
        const out = document.createElement('canvas');
        out.width = width;
        out.height = height;
        const context = out.getContext('2d');
        if (!context || !canvas.width || !canvas.height) {
            return { status: 500, body: envelope(false, 'The Designer canvas could not be read.') };
        }
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
        const scale = Math.min(width / canvas.width, height / canvas.height);
        const w = canvas.width * scale;
        const h = canvas.height * scale;
        context.drawImage(canvas, (width - w) / 2, (height - h) / 2, w, h);
        return { status: 200, png: out.toDataURL('image/png').split(',')[1] };
    }
}

export default DesignerDriver;
